import { uiTestId } from '@7f-ui/test-ids'
import type { ElementType, PropsWithChildren, ReactNode, Ref } from 'react'

import { cx } from '../cx'
import type { ButtonAsProps } from '../polymorphic-props'
import type { LoadingButtonProps } from './button/loading-button'
import { LoadingButton } from './button/loading-button'

type StickyFooterButtonProps<E extends ElementType> = LoadingButtonProps &
  ButtonAsProps<E> & {
    'data-testid'?: string
  }

export type StickyFooterProps<E extends ElementType> = {
  fixed?: boolean
  numberOfLines?: number
  buttonProps?: StickyFooterButtonProps<E>
  popup?: ReactNode
  ref?: Ref<any>
}

export function StickyFooter<TAs extends ElementType>({
  ref,
  children,
  fixed = true,
  numberOfLines = 2,
  buttonProps,
  popup,
}: PropsWithChildren<StickyFooterProps<TAs>>) {
  return (
    <div
      className={cx('inset-x-0 bottom-0 z-30 shadow-sticky-footer sm:hidden', {
        fixed,
      })}
      data-testid={uiTestId('StickyFooter.root')}
    >
      {popup}
      <div
        className={cx('flex justify-between bg-white p-4 xs:px-6 text-primary', {
          'text-primary/30': buttonProps?.disabled,
          'xs:py-5': numberOfLines === 2,
          'px-4 py-3': numberOfLines === 3,
        })}
      >
        <div className="flex-1">{children}</div>
        {buttonProps && (
          <div className="self-center" data-testid={uiTestId('StickyFooter.button')}>
            <LoadingButton ref={ref} {...buttonProps} />
          </div>
        )}
      </div>
    </div>
  )
}
